import { supabase } from '../lib/supabase'
import { getCurrentUserId, verifyPropertyAccess, verifyPropertyWriteAccess } from '../lib/auth'
import {
  createAuditLog,
  logScheduledMaintenanceCreated,
  logScheduledMaintenanceUpdated,
  logScheduledMaintenanceDeleted,
  logScheduledMaintenanceToggled,
} from '../lib/audit'
import {
  scheduledMaintenanceInsertSchema,
  scheduledMaintenanceUpdateSchema,
} from '../lib/validators'
import type {
  ScheduledMaintenanceTask,
  ScheduledMaintenanceInsert,
  ScheduledMaintenanceUpdate,
  ScheduledMaintenanceSummary,
} from '../types/scheduledMaintenance'

// ============================================================
// SELECT FRAGMENT
// ============================================================

const TASK_SELECT = `
  *,
  property:properties(id, address_line1, city, postcode),
  linked_issue:issues!last_generated_issue_id(id, status, reference_number, deleted_at)
`

// ============================================================
// QUERY FUNCTIONS
// ============================================================

/**
 * List all active scheduled maintenance tasks for the current user.
 * Optionally filtered by property.
 */
export async function getScheduledMaintenanceTasks(
  propertyId?: string
): Promise<ScheduledMaintenanceTask[]> {
  if (propertyId) {
    await verifyPropertyAccess(propertyId)
  } else {
    await getCurrentUserId() // ensure authenticated
  }

  let query = supabase
    .from('scheduled_maintenance_tasks')
    .select(TASK_SELECT)
    .is('deleted_at', null)
    .order('next_due_date', { ascending: true })

  if (propertyId) {
    query = query.eq('property_id', propertyId)
  }

  const { data, error } = await query

  if (error) throw error

  return enrichWithComputedFields(data || [])
}

/**
 * Get a single scheduled maintenance task by ID.
 */
export async function getScheduledMaintenanceTask(
  id: string
): Promise<ScheduledMaintenanceTask> {
  await getCurrentUserId() // ensure authenticated — RLS scopes result
  const { data, error } = await supabase
    .from('scheduled_maintenance_tasks')
    .select(TASK_SELECT)
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (error) throw error

  return enrichWithComputedFields([data])[0]
}

/**
 * Create a new scheduled maintenance task.
 */
export async function createScheduledMaintenanceTask(
  input: ScheduledMaintenanceInsert
): Promise<ScheduledMaintenanceTask> {
  const validated = scheduledMaintenanceInsertSchema.parse(input)
  await verifyPropertyWriteAccess(validated.property_id)

  // Always derive owner_id from properties.owner_id — never trust caller input.
  const { data: prop, error: propError } = await supabase
    .from('properties')
    .select('owner_id')
    .eq('id', validated.property_id)
    .single()
  if (propError || !prop) throw new Error('Property not found')

  const userId = await getCurrentUserId()

  const { data, error } = await supabase
    .from('scheduled_maintenance_tasks')
    .insert({
      owner_id: prop.owner_id,
      created_by: userId,
      property_id: validated.property_id,
      unit_id: validated.unit_id ?? null,
      asset_id: validated.asset_id ?? null,
      title: validated.title,
      description: validated.description ?? null,
      category: validated.category,
      urgency: validated.urgency,
      compliance_document_type: validated.compliance_document_type ?? null,
      preferred_contractor_id: validated.preferred_contractor_id ?? null,
      preferred_approved_contractor_id: validated.preferred_approved_contractor_id ?? null,
      frequency: validated.frequency,
      custom_interval_days: validated.custom_interval_days ?? null,
      next_due_date: validated.next_due_date,
      lead_time_days: validated.lead_time_days,
      estimated_cost_pence: validated.estimated_cost_pence ?? null,
      is_active: validated.is_active,
      notes: validated.notes ?? null,
    })
    .select(TASK_SELECT)
    .single()

  if (error) throw error

  await logScheduledMaintenanceCreated(data.id, {
    title: validated.title,
    category: validated.category,
    frequency: validated.frequency,
    next_due_date: validated.next_due_date,
    property_id: validated.property_id,
    compliance_document_type: validated.compliance_document_type ?? null,
  })

  // Sync calendar events to include the new task
  syncCalendarInBackground()

  return enrichWithComputedFields([data])[0]
}

/**
 * Update an existing scheduled maintenance task.
 */
export async function updateScheduledMaintenanceTask(
  id: string,
  input: ScheduledMaintenanceUpdate
): Promise<ScheduledMaintenanceTask> {
  const validated = scheduledMaintenanceUpdateSchema.parse(input)

  // Fetch old data for audit diff
  const { data: existing, error: fetchError } = await supabase
    .from('scheduled_maintenance_tasks')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (fetchError) throw fetchError
  if (!existing) throw new Error('Scheduled maintenance task not found')

  await verifyPropertyWriteAccess(existing.property_id)

  const updateData: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(validated)) {
    if (value !== undefined) {
      updateData[key] = value ?? null
    }
  }

  const { data, error } = await supabase
    .from('scheduled_maintenance_tasks')
    .update(updateData)
    .eq('id', id)
    .is('deleted_at', null)
    .select(TASK_SELECT)
    .single()

  if (error) throw error

  // Build audit diff — only include changed fields
  const oldData: Record<string, unknown> = {}
  const newData: Record<string, unknown> = {}
  for (const key of Object.keys(updateData)) {
    if (existing[key] !== data[key]) {
      oldData[key] = existing[key]
      newData[key] = data[key]
    }
  }

  if (Object.keys(oldData).length > 0) {
    await logScheduledMaintenanceUpdated(id, oldData, newData)
  }

  syncCalendarInBackground()

  return enrichWithComputedFields([data])[0]
}

/**
 * Soft delete a scheduled maintenance task.
 */
export async function deleteScheduledMaintenanceTask(
  id: string
): Promise<void> {
  const { data: existing, error: fetchError } = await supabase
    .from('scheduled_maintenance_tasks')
    .select('title, category, property_id')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (fetchError) throw fetchError
  if (!existing) throw new Error('Scheduled maintenance task not found')

  await verifyPropertyWriteAccess(existing.property_id)

  const { error } = await supabase
    .from('scheduled_maintenance_tasks')
    .update({ deleted_at: new Date().toISOString(), is_active: false })
    .eq('id', id)

  if (error) throw error

  await logScheduledMaintenanceDeleted(id, {
    title: existing.title,
    category: existing.category,
    property_id: existing.property_id,
  })

  syncCalendarInBackground()
}

/**
 * Pause or resume a scheduled maintenance task.
 */
export async function toggleScheduledMaintenanceActive(
  id: string,
  isActive: boolean
): Promise<ScheduledMaintenanceTask> {
  // Fetch to verify ownership before toggling
  const { data: existing, error: fetchError } = await supabase
    .from('scheduled_maintenance_tasks')
    .select('property_id')
    .eq('id', id)
    .is('deleted_at', null)
    .single()
  if (fetchError || !existing) throw new Error('Scheduled maintenance task not found')
  await verifyPropertyWriteAccess(existing.property_id)

  const { data, error } = await supabase
    .from('scheduled_maintenance_tasks')
    .update({ is_active: isActive })
    .eq('id', id)
    .is('deleted_at', null)
    .select(TASK_SELECT)
    .single()

  if (error) throw error

  await logScheduledMaintenanceToggled(id, isActive)

  syncCalendarInBackground()

  return enrichWithComputedFields([data])[0]
}

/**
 * Get scheduled maintenance tasks for a specific property (summary format).
 */
export async function getScheduledMaintenanceByProperty(
  propertyId: string
): Promise<ScheduledMaintenanceSummary[]> {
  await verifyPropertyAccess(propertyId)
  const { data, error } = await supabase
    .from('scheduled_maintenance_tasks')
    .select(`
      id, title, category, frequency, next_due_date, is_active,
      compliance_document_type, last_completed_date,
      property:properties(address_line1),
      asset:assets(name)
    `)
    .eq('property_id', propertyId)
    .is('deleted_at', null)
    .order('next_due_date', { ascending: true })

  if (error) throw error

  const today = new Date().toISOString().split('T')[0]

  return (data || []).map((row) => ({
    id: row.id,
    title: row.title,
    category: row.category as ScheduledMaintenanceSummary['category'],
    frequency: row.frequency as ScheduledMaintenanceSummary['frequency'],
    next_due_date: row.next_due_date,
    is_active: row.is_active,
    property_name: (row.property as { address_line1: string } | null)?.address_line1 || '',
    asset_name: (row.asset as { name: string } | null)?.name || null,
    days_until_due: daysDiff(today, row.next_due_date),
    is_overdue: row.next_due_date < today,
    compliance_document_type: row.compliance_document_type,
    last_completed_date: row.last_completed_date,
  }))
}

/**
 * Get scheduled maintenance tasks linked to a specific asset.
 */
export async function getScheduledMaintenanceByAsset(
  assetId: string
): Promise<ScheduledMaintenanceSummary[]> {
  const { data, error } = await supabase
    .from('scheduled_maintenance_tasks')
    .select(`
      id, title, category, frequency, next_due_date, is_active,
      compliance_document_type, last_completed_date,
      property:properties(address_line1),
      asset:assets(name)
    `)
    .eq('asset_id', assetId)
    .is('deleted_at', null)
    .order('next_due_date', { ascending: true })

  if (error) throw error

  const today = new Date().toISOString().split('T')[0]

  return (data || []).map((row) => ({
    id: row.id,
    title: row.title,
    category: row.category as ScheduledMaintenanceSummary['category'],
    frequency: row.frequency as ScheduledMaintenanceSummary['frequency'],
    next_due_date: row.next_due_date,
    is_active: row.is_active,
    property_name: (row.property as { address_line1: string } | null)?.address_line1 || '',
    asset_name: (row.asset as { name: string } | null)?.name || null,
    days_until_due: daysDiff(today, row.next_due_date),
    is_overdue: row.next_due_date < today,
    compliance_document_type: row.compliance_document_type,
    last_completed_date: row.last_completed_date,
  }))
}

/**
 * Get tasks due within N days for the dashboard widget.
 */
export async function getUpcomingScheduledMaintenance(
  days: number = 30
): Promise<ScheduledMaintenanceTask[]> {
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() + days)
  const cutoffStr = cutoff.toISOString().split('T')[0]

  const { data, error } = await supabase
    .from('scheduled_maintenance_tasks')
    .select(TASK_SELECT)
    .eq('is_active', true)
    .is('deleted_at', null)
    .lte('next_due_date', cutoffStr)
    .order('next_due_date', { ascending: true })

  if (error) throw error

  return enrichWithComputedFields(data || [])
}

// ============================================================
// ISSUE CREATION FROM TASK
// ============================================================

const CATEGORY_MAP: Record<string, string> = {
  fire_safety: 'other', gas_safety: 'other', general_inspection: 'other',
}

/** Manually create a maintenance issue from a scheduled task. */
export async function createIssueFromScheduledTask(
  taskId: string
): Promise<{ issueId: string; referenceNumber: string }> {
  const { data: task, error: taskError } = await supabase
    .from('scheduled_maintenance_tasks')
    .select('*')
    .eq('id', taskId).is('deleted_at', null).single()
  if (taskError || !task) throw new Error('Scheduled maintenance task not found')

  await verifyPropertyWriteAccess(task.property_id)
  const userId = await getCurrentUserId()

  const issueCategory = CATEGORY_MAP[task.category] || task.category
  const desc = [
    task.description || '',
    `Created from scheduled maintenance task (${task.frequency}).`,
    task.compliance_document_type ? `Compliance type: ${task.compliance_document_type}` : '',
  ].filter(Boolean).join('\n')

  // Resolve the primary tenant for access scheduling
  const { data: accessContact } = await supabase.rpc('resolve_access_contact', {
    p_property_id: task.property_id,
  })

  const { data: issue, error: issueError } = await supabase
    .from('issues')
    .insert({
      property_id: task.property_id, unit_id: task.unit_id, asset_id: task.asset_id,
      category: issueCategory, title: task.title, description: desc,
      urgency: task.urgency, reported_by: userId,
      access_contact_id: accessContact || null,
      status: 'acknowledged', reported_at: new Date().toISOString(),
    })
    .select('id, reference_number').single()
  if (issueError) throw issueError

  await supabase.from('scheduled_maintenance_tasks')
    .update({ last_generated_issue_id: issue.id }).eq('id', taskId)

  if (task.preferred_contractor_id || task.preferred_approved_contractor_id) {
    const assign: Record<string, string | null> = { status: 'acknowledged' }
    if (task.preferred_contractor_id) assign.assigned_to = task.preferred_contractor_id
    if (task.preferred_approved_contractor_id) assign.assigned_approved_contractor_id = task.preferred_approved_contractor_id
    await supabase.from('issues').update(assign).eq('id', issue.id)
  }

  // `scheduled_maintenance_created` is emitted by
  // notify_schedule_issue_created() (migration 389), the trigger on the
  // last_generated_issue_id write above — the moment "a job was created
  // from this schedule" first becomes true server-side. It addresses the
  // task's owner_id, not the caller, which is what the browser passed.
  await createAuditLog({
    action: 'scheduled_maintenance_issue_created',
    entityType: 'issue', entityId: issue.id,
    metadata: { task_id: taskId, task_title: task.title, issue_reference: issue.reference_number, manual: true },
  })
  syncCalendarInBackground()
  return { issueId: issue.id, referenceNumber: issue.reference_number }
}

// ============================================================
// COMPLETION HOOK
// ============================================================

/**
 * Called when a generated issue reaches 'closed' or 'completed' status.
 * Advances the schedule to the next due date, notifies the owner, and
 * syncs calendar events. Fire-and-forget — errors logged but not thrown.
 */
export async function handleScheduledMaintenanceIssueCompleted(
  issueId: string
): Promise<void> {
  try {
    // Find the scheduled task that generated this issue
    const { data: task, error: taskError } = await supabase
      .from('scheduled_maintenance_tasks')
      .select('id, owner_id, title, next_due_date, frequency')
      .eq('last_generated_issue_id', issueId)
      .is('deleted_at', null)
      .maybeSingle()

    if (taskError || !task) return // No linked task — nothing to do

    // Advance the schedule via RPC
    const { data: newDueDate, error: advanceError } = await supabase
      .rpc('advance_scheduled_maintenance', {
        p_task_id: task.id,
        p_completed_date: new Date().toISOString().split('T')[0],
      })

    if (advanceError) {
      console.error('advance_scheduled_maintenance error:', advanceError.message)
      return
    }

    // The owner's `scheduled_maintenance_completed` is emitted inside
    // advance_scheduled_maintenance (migration 389), which already holds
    // the task and the new due date.

    // Audit log
    await createAuditLog({
      action: 'scheduled_maintenance_completed',
      entityType: 'scheduled_maintenance_task',
      entityId: task.id,
      metadata: {
        issue_id: issueId,
        task_title: task.title,
        previous_due_date: task.next_due_date,
        new_due_date: newDueDate,
      },
    })

    // Sync calendar
    syncCalendarInBackground()
  } catch (err) {
    console.error('handleScheduledMaintenanceIssueCompleted error:', err)
  }
}

// ============================================================
// HELPERS
// ============================================================

function daysDiff(fromIso: string, toIso: string): number {
  const from = new Date(fromIso)
  const to = new Date(toIso)
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
}

function enrichWithComputedFields(
  rows: Record<string, unknown>[]
): ScheduledMaintenanceTask[] {
  const today = new Date().toISOString().split('T')[0]

  return rows.map((row) => ({
    ...row,
    linked_issue: (row.linked_issue as { deleted_at?: string | null } | null)?.deleted_at ? null : row.linked_issue,
    days_until_due: daysDiff(today, row.next_due_date as string),
    is_overdue: (row.next_due_date as string) < today,
  })) as ScheduledMaintenanceTask[]
}

/**
 * Fire-and-forget calendar sync — non-blocking.
 *
 * Takes no user: migration 433 made the RPC zero-argument and derives the user
 * from auth.uid(). It used to accept an id and trust it, which is what let any
 * logged-in caller rebuild someone else's calendar.
 *
 * CONSEQUENCE, deliberate: this now syncs the CALLER'S calendar, never another
 * user's. `handleScheduledMaintenanceIssueCompleted` used to pass
 * `task.owner_id`, so when a manager or contractor closed a job the owner's
 * calendar was refreshed for them. That cross-user write was the IDOR, so it
 * is gone. The owner's calendar is self-healing: `Calendar.tsx` syncs on mount,
 * so it is correct the next time they look at it — and the call was already
 * fire-and-forget with a swallowed error, so it was never a guarantee.
 */
function syncCalendarInBackground(): void {
  supabase
    .rpc('sync_scheduled_maintenance_calendar_events')
    .then(({ error }) => {
      if (error) console.error('Calendar sync error:', error.message)
    })
    .catch(() => {})
}
